import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import FileDropzone from "../fileAdzone";
import { Separator } from "@/components/ui/separator";
import CTAButton from "../ctaButton";
import TextArea from "../textAera";
import Preview from "../previewScreen";
import { FormData } from "@/components/types/internal/types/FormDataTypes";
import useStore from "@/zustand/store";

/**
 * Component to create and manage advertisement content.
 * Utilizes react-hook-form for form state management and Zustand for global state.
 * @returns JSX.Element
 */
const CreateAd = () => {
  // Extract methods and state from react-hook-form context
  const {
    control,
    setValue,
    watch,
    register,
    resetField,
    formState: { errors },
  } = useFormContext<FormData>();

  // Access Zustand store actions for updating advertisement data
  const setAdImage = useStore((state) => state.setAdImage);
  const setCtaButtonTitle = useStore((state) => state.setCtaButtonTitle);
  const [uploadMode, setUploadMode] = useState<"separate" | "single">("separate");


  // Watch form values to update Zustand store accordingly
  const adImage = watch("adImage");
  const ctaButtonTitle = watch("ctaButtonTitle");
  const adText = watch("adText");
  const discountCode = watch("discountCode");
  const adName = watch("adName");
  const selectedLeadCardTitle = watch("selectedLeadCardTitle"); // Watch the goal
  const iosLink = watch("iosLink");
  const androidLink = watch("androidLink");

  /**
   * Synchronizes the ad image with Zustand store whenever it changes.
   */
  useEffect(() => {
    setAdImage(adImage);
  }, [adImage, setAdImage]);
  /**
   * Synchronizes the CTA button title with Zustand store whenever it changes.
   */
  useEffect(() => {
    setCtaButtonTitle(ctaButtonTitle);
  }, [ctaButtonTitle, setCtaButtonTitle]);
  const handleToggleMode = (mode: "separate" | "single") => {
    setUploadMode(mode);

    if (mode === "separate") {
      resetField("singleFile"); // Resets field value to default
    } else {
      resetField("adImage");
      resetField("backgroundImage");
      resetField("adAudio");
    }
  };

  return (
    <div className="bg-gray-50 flex flex-col md:flex-row lg:flex-row justify-center md:gap-2 md:items-start lg:items-start gap-10 lg:gap-20 px-4 py-14 lg:py-1 dark:bg-background overflow-hidden">
      <Card className="bg-gray-100 p-8 rounded-lg border border-gray-300 shadow-lg lg:w-1/2 dark:bg-background">
        <h3 className="text-xl lg:text-2xl font-semibold font-Inter pl-6 lg:pb-2">
          Create AD
        </h3>
        <CardContent>
          <Separator className="my-2 bg-gray-300" />
          <h4 className="text-base lg:text-xl font-semibold py-3 font-Inter">
            Front Side
          </h4>
          <div className="mb-4">
            <div className="flex flex-col lg:flex-row justify-center items-center mb-6 -mx-8">
              <div className="lg:border border-gray-300 rounded-full font-Inter">
                <button
                  className={` w-full lg:w-auto px-4 py-3 mb-4 lg:mb-0 text-sm font-medium rounded-full lg:rounded-r-none ${uploadMode === "separate" ? "bg-Primary text-[#8E4400]" : "bg-white"
                    }`}
                  onClick={() => handleToggleMode("separate")}
                >
                  Add Separate Files
                </button>

                <button
                  className={`w-full lg:w-auto px-4 py-3 text-sm font-medium rounded-full lg:rounded-l-none ${uploadMode === "single" ? "bg-Primary text-[#8E4400]" : "bg-white"
                    }`}
                  onClick={() => handleToggleMode("single")}
                >
                  Add a Single Completed File
                </button>
              </div>
            </div>
            {uploadMode === "separate" ? (
              <>
                <FileDropzone
                  label="Upload Ad Image"
                  accept={{ "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] }}
                  maxFiles={1}
                  control={control}
                  name="adImage"
                  required
                />
                <p className="text-xs font-normal text-[#64748B] ml-1 -mt-2 mb-4">For best output use image of 228px * 428px</p>
                <Separator className="my-2 bg-gray-300" />
                <FileDropzone
                  label="Upload  Audio"
                  accept={{ "audio/mpeg": [".mp3"], "audio/wav": [".wav"], "audio/ogg": [".ogg"] }}
                  maxFiles={1}
                  control={control}
                  name="adAudio"
                  required
                />
              </>
            ) : (
              <FileDropzone
                label="Upload Completed Ad File"
                accept={{ "video/mp4": [".mp4"], }}
                maxFiles={1}
                control={control}
                name="singleFile"
                required
              />
            )}
          </div>
          <CTAButton
            label="Ad Name"
            value={adName}
            onChange={(value) => {
              // Trim spaces before setting the value
              setValue("adName", value.trim(), { shouldValidate: true });
            }}
            placeholder="Enter Ad Name"
            register={register("adName", {
              required: "Ad Name is required",
              validate: (value) => value.trim() !== "" || "Ad Name cannot be empty or just spaces",
            })}
          />
          {errors.adName && (
            <p className="text-red-500 text-sm pb-2 px-2">
              {errors.adName.message}
            </p>
          )}
          <Separator className="my-2 bg-gray-300" />
          <div>
            <h4 className="text-base lg:text-xl font-semibold py-3 font-Inter">
              CTA/Button
            </h4>
            {/* CTA Button for setting the button title with validation */}
            <CTAButton
              label="Button Title"
              value={ctaButtonTitle}
              onChange={(title) => {
                // Trim spaces before setting the value
                const trimmedTitle = title.trim();
                setValue("ctaButtonTitle", trimmedTitle, { shouldValidate: true });
                setCtaButtonTitle(trimmedTitle); // Update Zustand store
              }}
              placeholder="Enter button title here"
              maxLength={15}
              register={register("ctaButtonTitle", {
                required: "Button title is required",
                validate: (title) => title.trim() !== "" || "Button title cannot be empty or just spaces",
                maxLength: {
                  value: 15,
                  message: "Button title must be less than 15 characters",
                },
              })}
            />

            {/* Display validation error message for button title */}
            {errors.ctaButtonTitle && (
              <p className="text-red-500 text-sm pb-2 px-2">
                {errors.ctaButtonTitle.message}
              </p>
            )}

            {selectedLeadCardTitle === "App downloads" && (
              <>
                {/* iOS App Link */}
                <CTAButton
                  label="IOS App Link"
                  value={iosLink}
                  onChange={(value) =>
                    setValue("iosLink", value, { shouldValidate: true })
                  }
                  placeholder="Enter iOS App Store URL"
                  register={register("iosLink", {
                    required: "IOS link is required",
                    pattern: {
                      value: /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/i,
                      message:
                        "Please enter a valid URL, starting with http:// or https://",
                    },
                  })}
                />
                {errors.iosLink && (
                  <p className="text-red-500 text-sm pb-2 px-2">
                    {errors.iosLink.message}
                  </p>
                )}

                {/* Android App Link */}
                <CTAButton
                  label="Android App Link"
                  value={androidLink}
                  onChange={(value) =>
                    setValue("androidLink", value, { shouldValidate: true })
                  }
                  placeholder="Enter Android Play Store URL"
                  register={register("androidLink", {
                    required: "Android link is required",
                    pattern: {
                      value: /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/i,
                      message:
                        "Please enter a valid URL, starting with http:// or https://",
                    },
                  })}
                />
                {errors.androidLink && (
                  <p className="text-red-500 text-sm pb-2 px-2">
                    {errors.androidLink.message}
                  </p>
                )}
              </>
            )}

            {selectedLeadCardTitle !== "App downloads" && (
              <CTAButton
                label="Button URL"
                value={watch("buttonUrl")}
                onChange={(value) =>
                  setValue("buttonUrl", value, { shouldValidate: true })
                }
                placeholder="Enter URL here"
                maxLength={200}
                register={register("buttonUrl", {
                  required: "Button URL is required",
                  pattern: {
                    value: /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/i,
                    message:
                      "Please enter a valid URL, starting with http:// or https://",
                  },
                })}
              />
            )}
            {/* Display validation error message for button URL */}
            {errors.buttonUrl && (
              <p className="text-red-500 text-sm pb-2 px-2">
                {errors.buttonUrl.message}
              </p>
            )}
          </div>
          <Separator className="my-2 bg-gray-300" />
          <div>
            <h2 className="text-base lg:text-xl font-semibold py-3 font-Inter">
              Back Side
            </h2>
            {/* TextArea for adding coupon text with validation */}
            <TextArea
              label="Add Text"
              value={adText}
              onChange={(value) => {
                // Trim spaces before setting the value
                setValue("adText", value.trim(), { shouldValidate: true });
              }}
              placeholder="Text here"
              maxLength={100}
              register={register("adText", {
                required: "Ad Text is required",
                maxLength: {
                  value: 100,
                  message: "Ad Text must be less than 100 characters",
                },
                validate: (value) => value.trim() !== "" || "Ad Text cannot be empty or just spaces",
              })}
            />

            {/* Display validation error message for coupon text */}
            {errors.adText && (
              <p className="text-red-500 text-sm pb-2 px-2">
                {errors.adText.message}
              </p>
            )}
            {/* CTA Button for adding discount codes with validation */}
            <CTAButton
              label="Add Discount Codes"
              value={discountCode}
              onChange={(value) =>
                setValue("discountCode", value, { shouldValidate: true })
              }
              placeholder="Enter code here"
              register={register("discountCode", {
                required: "Discount code is required",
                pattern: {
                  value: /^JAM(?=.*[A-Z])(?=.*[0-9])[A-Z0-9]{5}ME$/,
                  message:
                    "Coupon code must start with 'JAM', contain exactly 5 alphanumeric characters (at least one letter and one number), and end with 'ME'.",
                },
              })}
              required={true} // Will display "(optional)"
            />

            {/* Display validation error message for coupon code */}
            {errors.discountCode && (
              <p className="text-red-500 text-sm pb-2 px-2">
                {errors.discountCode.message}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
      {/* Preview component for displaying the advertisement */}
      <div>
        <Preview />
      </div>
    </div>
  );
};

export default CreateAd;
